import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LocationModel } from "@NA/Hooks/useLocationFilterSearchCustom";
import ADIColors from "@NA/Utils/ADIColors";
import * as React from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: ADIBranchSearchFormStyles;
    onSearch: (searchGeoLocationFilter: string, searchLocationFilter: string) => void;
    onSelect: (location: LocationModel) => void;
    locationSearchFilter: string;
    geoLocationSearchText: string;
    showGeoLocationErrorMessage: boolean;
    geoLocationErrorMessage: React.ReactNode;
    isLoading: boolean;
    locations: LocationModel[];
    setRadius: (radius: number) => void;
    isSearchClicked: boolean;
}

type Props = OwnProps;

export interface ADIBranchSearchFormStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    form?: InjectableCss;
    searchContainer?: InjectableCss;
    searchInputRow?: InjectableCss;
    searchInputGridContainer: GridContainerProps;
    searchGeoLocationGridItem?: GridItemProps;
    searchGeoLocationText?: TextFieldPresentationProps;
    selectWrapper?: InjectableCss;
    btnWrapper?: InjectableCss;
    searchButtonGridItem?: GridItemProps;
    searchButton?: ButtonPresentationProps;
    searchResultGridItem?: GridItemProps;
    searchResultRow?: InjectableCss;
    searchResultRowContainer?: InjectableCss;
    searchResultBranchName?: InjectableCss;
    locationNameTxt?: TypographyPresentationProps;
    searchResultSelectBtnWrapper?: InjectableCss;
}

export const adiBranchSearchFormStyles: ADIBranchSearchFormStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    form: {
        css: css`
            width: 100%;
        `,
    },
    searchContainer: {
        css: css`
            display: grid;
        `,
    },
    searchInputRow: {
        css: css`
            padding: 8px;
        `,
    },
    searchInputGridContainer: {
        gap: 10,
    },
    searchGeoLocationGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    selectWrapper: {
        css: css`
            width: 60%;
        `,
    },
    btnWrapper: {
        css: css`
            height: 100%;
            display: flex;
            align-items: flex-end;
        `,
    },
    searchButtonGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            justify-content: space-between;
      a{
        span
            {
            color: blue !important;
            font-size: 12px;
            text-decoration: underline;
            font-weight:bold;
            }
      }
        `,
    },
    searchButton: {
        css: css`
            border: 2px solid #c71e1e;
            background-color: #c71e1e;
        `,
    },
    searchResultGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    searchResultRow: {
        css: css`
            padding: 8px;
            max-height: 200px;
            overflow-y: auto;
            overflow-x: hidden;
            ${({ theme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        max-height: 350px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ],
            )}
        `,
    },
    searchResultRowContainer: {
        css: css`
            width: 50%;
            display: flex;
            flex-direction: row;
            border-bottom: 1px solid ${ADIColors.gray15};
            padding: 10px 0 5px 10px;
            ${({ theme }) =>
            breakpointMediaQueries(
                theme,
                    [
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ],
            )}
        `,
    },
    searchResultBranchName: {
        css: css`
            width: 50%;
            text-align: left;
            font-size: 14px;
        `,
    },
    locationNameTxt: {
        weight: "bold",
        ellipsis: true,
        color: ADIColors.darkBlue1,
        css: css`
            width: 100%;
        `,
    },
    searchResultSelectBtnWrapper: {
        css: css`
            width: 50%;
            text-align: right;
            button:disabled {
                color: ${ADIColors.blue1} !important;
                border-color: ${ADIColors.blue1} !important;
                cursor: pointer !important;
            }
        `,
    },
};

const StyledForm = getStyledWrapper("form");

const Container = StyledWrapper;
const ContainerCell = StyledWrapper;

const styles = adiBranchSearchFormStyles;

const ADIBranchSearchForm: React.FC<Props> = ({
    onSearch,
    onSelect,
    locationSearchFilter,
    geoLocationSearchText,
    showGeoLocationErrorMessage,
    geoLocationErrorMessage,
    isLoading,
    locations,
    setRadius,
    isSearchClicked,
}) => {
    const [searchLocationFilter, setSearchLocationFilter] = React.useState(locationSearchFilter);
    const [searchGeoLocationFilter, setSearchGeoLocationFilter] = React.useState(geoLocationSearchText);
    React.useEffect(() => {
        setSearchLocationFilter(locationSearchFilter);
    }, [locationSearchFilter]);
    React.useEffect(() => {
        setSearchGeoLocationFilter(geoLocationSearchText);
    }, [geoLocationSearchText]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isLoading) {
            onSearch(searchGeoLocationFilter, searchLocationFilter);
        }
    };
    const handleSearchGeoLocationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchGeoLocationFilter(event.target.value);
    };

    return (
        <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
            <Container {...styles.searchContainer}>
                <ContainerCell {...styles.searchInputRow}>
                    <GridContainer {...styles.searchInputGridContainer}>
                        <GridItem {...styles.searchButtonGridItem}>
                            <Link target="_blank" href="/dealerlocator">{translate("Directions >")}</Link>
                        </GridItem>
                    </GridContainer>
                    <GridContainer {...styles.searchInputGridContainer}>
                        <GridItem {...styles.searchGeoLocationGridItem}>
                            <TextField
                                id="searchGeoLocation"
                                {...styles.searchGeoLocationText}
                                label={translate("Location:")}
                                required={true}
                                value={searchGeoLocationFilter}
                                onChange={handleSearchGeoLocationChanged}
                                data-test-selector="locationSearchForm_searchGeoLocation"
                                error={showGeoLocationErrorMessage ? geoLocationErrorMessage : undefined}
                            />
                        </GridItem>
                        <GridItem {...styles.searchButtonGridItem}>
                            <StyledWrapper {...styles.selectWrapper}>
                                <Select
                                    name="miles"
                                    id="miles"
                                    label={translate("Within:")}
                                    onChange={e => {
                                        setRadius(Number(e.target.value));
                                    }}
                                >
                                    <option value="100">Within 100 Miles</option>
                                    <option value="200">Within 200 Miles</option>
                                    <option value="300">Within 300 Miles</option>
                                    <option value="500">Within 500 Miles</option>
                                </Select>
                            </StyledWrapper>
                            <StyledWrapper {...styles.btnWrapper}>
                                <Button
                                    {...styles.searchButton}
                                    type="submit"
                                    data-test-selector="branchSearchForm_searchButton"
                                >
                                    {translate("Search")}
                                </Button>
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </ContainerCell>

                <ContainerCell {...styles.searchResultRow}>
                    <GridContainer>
                        {isLoading && locations.length === 0 && (
                            <GridItem {...styles.searchResultGridItem}>
                                <StyledWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {isSearchClicked && locations.map((location, index) => (
                                <GridItem key={location.id} {...styles.searchResultGridItem}>
                                    <StyledWrapper
                                        {...styles.searchResultRowContainer}
                                        data-test-selector="branchSearchForm"
                                        data-test-key={`${location.id}`}
                                    >
                                        <StyledWrapper {...styles.searchResultBranchName}>
                                            <StyledWrapper>
                                            <Typography
                                                {...styles.locationNameTxt}
                                            >
                                                {location.name}
                                            </Typography>
                                            </StyledWrapper>
                                            <Typography>{`${location.city}, ${location.state} ${location.postalCode}`}</Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.searchResultSelectBtnWrapper}>
                                            <Button variant="secondary" disabled={!location.webSiteUrl} onClick={() => onSelect(location)}>{translate("Select")}</Button>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            ))}
                    </GridContainer>
                </ContainerCell>
            </Container>
        </StyledForm>
    );
};

export default (ADIBranchSearchForm);
